// this file only load and return a useEditor()

import { symRoundedEdit } from '@quasar/extras/material-symbols-rounded'
import { destr } from 'destr'
import useCrudUtils from 'src/CrudManager/Core/composables/CrudUtils/useCrudUtils'
import type { CrudEditorData, CrudInstance, CrudModule } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { defineAsyncComponent } from 'vue'

// Local interface to access title/subtitle getters without strict generic constraints
interface EditorConfigWithTitles {
  getTitle?: (item: unknown) => string | Promise<string>
  getSubtitle?: (item: unknown) => string | Promise<string>
}

export default {
  name: 'editor',
  slugs: ['edit', 'editer'],
  icon: symRoundedEdit,
  mainComponent: defineAsyncComponent(() => import('./MainComponent.vue')),

  boot: (instance) => {
    const EditorInstance = instance as CrudInstance

    const instanceData = EditorInstance.data
    const props = EditorInstance.props
    const itemId = props && props.modelId ? props.modelId : null

    const previousItem = (instanceData?.item ?? {}) as Record<string, unknown>
    const previousOriginalItem = (instanceData?.originalItem ?? {}) as Record<string, unknown>
    const matchesId = previousItem.id === itemId
    const hasPersistedFields = Object.keys(previousItem).some((k) => k !== 'id')
    const reuseItem = matchesId && hasPersistedFields

    const data = {
      ...instanceData,
      item: reuseItem ? previousItem : { id: itemId as number },
      originalItem: reuseItem && previousOriginalItem.id === itemId ? previousOriginalItem : { id: itemId as number },
      editmodel: 'edit',
    }

    EditorInstance.data = data

    // Skip title/subtitle rebuild when item is just an id stub; otherwise
    // we'd overwrite any persisted title with `.undefined` placeholders.
    // displayInstance will rebuild them after the fetch completes.
    if (!reuseItem) {
      return EditorInstance
    }

    // Initialize title and subtitle without blocking boot return
    ;(async () => {
      try {
        const { getModelConfig } = useCrudUtils()
        const config = await getModelConfig(EditorInstance.modelName)
        const editorConfig = config?.editor as EditorConfigWithTitles | undefined

        if (!editorConfig) return

        if (editorConfig.getTitle) {
          const titleResult = editorConfig.getTitle(data.item)
          EditorInstance.title = titleResult instanceof Promise ? await titleResult : titleResult
        }

        if (editorConfig.getSubtitle) {
          const subtitleResult = editorConfig.getSubtitle(data.item)
          EditorInstance.subtitle = subtitleResult instanceof Promise ? await subtitleResult : subtitleResult
        }
      } catch (error) {
        console.warn(`Could not load model config for editor titles: ${EditorInstance.modelName}`, error)
      }
    })()

    return EditorInstance
  },

  save: async (instance) => {
    const EditorInstance = instance as CrudInstance
    const { getModelMeta } = useCrudUtils()
    const instanceData = EditorInstance.data as CrudEditorData

    try {
      const modelMeta = getModelMeta(EditorInstance.modelName)
      if (!modelMeta) {
        console.error(`Could not load model metadata: ${EditorInstance.modelName}`)
        return false
      }

      if (instanceData.item.id) {
        // Use new RESTful API pattern: PATCH /{model}/{id}
        const { data } = await axiosApi.patch(`${modelMeta.apiUrl}/${instanceData.item.id}`, {
          data: instanceData.item,
        })

        if (data.result !== 'success') return false

        instanceData.originalItem = destr(JSON.stringify(instanceData.item))
        return true
      }

      // Create new item using new RESTful API pattern: POST /{model}
      await axiosApi.post(`${modelMeta.apiUrl}`, {
        data: instanceData.item,
      })

      return true
    } catch (error) {
      console.error('Save failed', error)
      return error
    }
  },

  reset: (instance) => {
    const EditorInstance = instance
    const instanceData = EditorInstance.data as CrudEditorData
    instanceData.item = destr(JSON.stringify(instanceData.originalItem))
  },
} as CrudModule
